import type React from "react";
import {useState} from "react";
import {Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography} from "@mui/material";
import {PATH_SEP} from "~/constants";

type TaxonomyAddCategoryDialogProps = {
    /** Names from L1 down to the parent the category is added under; empty for a new L1. */
    parentPath: string[];
    /** Names already used under that parent: a new name must differ (case-insensitive). */
    siblingNames: string[];
    /** Set when the parent is on the deepest level: the level number the category creates. */
    newLevel?: number;
    isSaving: boolean;
    onCancel: () => void;
    onSave: (name: string, description: string) => void;
};

/**
 * The design's "Add category" dialog: name (required, unique under the parent) and an optional
 * description. Mount it fresh for each add, so the fields start empty.
 */
export const TaxonomyAddCategoryDialog: React.FC<TaxonomyAddCategoryDialogProps> = ({
                                                                                        parentPath,
                                                                                        siblingNames,
                                                                                        newLevel,
                                                                                        isSaving,
                                                                                        onCancel,
                                                                                        onSave,
                                                                                    }) => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const trimmed = name.trim();
    const isL1 = parentPath.length === 0;
    const parentName = parentPath.at(-1) ?? 'L1';
    const duplicate = trimmed !== '' && siblingNames.some(s => s.toLowerCase() === trimmed.toLowerCase());
    const canSave = trimmed !== '' && !duplicate && !isSaving;
    const save = () => {
        if (canSave) onSave(trimmed, description.trim());
    };

    return (
        <Dialog open onClose={isSaving ? undefined : onCancel} maxWidth="xs" fullWidth>
            <DialogTitle>Add category</DialogTitle>
            <DialogContent>
                <Stack gap={2} sx={{pt: 0.5}}>
                    <Stack gap={0.5}>
                        <Typography variant="caption" color="text.secondary">Parent path</Typography>
                        <Typography variant="body2" fontWeight={isL1 ? 400 : 600} color={isL1 ? 'text.secondary' : 'text.primary'}>
                            {isL1 ? 'It becomes an L1 category' : parentPath.join(PATH_SEP)}
                        </Typography>
                    </Stack>
                    {newLevel !== undefined && (
                        <Alert severity="info">This adds a new level to the taxonomy: L{newLevel}.</Alert>
                    )}
                    <TextField
                        label="New category name"
                        placeholder="e.g. Welding consumables"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter') save();
                        }}
                        error={duplicate}
                        helperText={duplicate ? `A category with this name already exists ${isL1 ? 'at' : 'under'} ${parentName}.` : undefined}
                        autoFocus
                        fullWidth
                        size="small"
                    />
                    <TextField
                        label="Description"
                        placeholder="What belongs in this category (optional)"
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                        multiline
                        minRows={3}
                        fullWidth
                        size="small"
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel} disabled={isSaving}>Cancel</Button>
                <Button variant="contained" onClick={save} loading={isSaving} disabled={!canSave}>
                    Add category
                </Button>
            </DialogActions>
        </Dialog>
    );
};
